"use client";

import { useAuth } from "@/components/features/auth/hooks/use-auth";
import { useLocationPrefs } from "@/components/features/location/hooks/use-location-prefs";
import { analytics } from "@/lib/analytics/ga4";
import { routes } from "@/lib/routes";
import { Logo } from "@workspace/brand/components/logo";
import { Badge } from "@workspace/ui/components/badge";
import { Button } from "@workspace/ui/components/button";
import { cn } from "@workspace/ui/lib/utils";
import {
  Dialog,
  DialogDescription,
  DialogHeader,
  DialogPopup,
  DialogTitle,
} from "@workspace/ui/v1/components/dialog-glass";
import { CheckCircle2, Circle } from "lucide-react";
import Link from "next/link";
import { useRouter } from "next/navigation";
import { useQueryState } from "nuqs";
import { useCallback, useEffect, useMemo, useRef, useState } from "react";
import {
  countCompletedTasks,
  getOnboardingTasks,
  type OnboardingTask,
} from "./new-user-todo-tasks";

type CloseReason = "skip" | "dismiss" | "complete";

interface ModalContentProps {
  generatedUsername?: string;
  onDismiss: () => void;
}

function ModalContent({ generatedUsername, onDismiss }: ModalContentProps) {
  const { user, userProfile, activeOrg, isAuthenticated, loaded } = useAuth();
  const { data: locationPrefs } = useLocationPrefs();
  const router = useRouter();
  const [open, setOpen] = useState(true);
  const hasClosedRef = useRef(false);
  const [hasLoggedImpression, setHasLoggedImpression] = useState(false);

  useEffect(() => {
    if (loaded && !isAuthenticated) {
      onDismiss();
    }
  }, [isAuthenticated, loaded, onDismiss]);

  const postalCode = useMemo(() => {
    const fromLocationPrefs = locationPrefs?.postalCode?.trim();
    const fromProfile =
      userProfile?.locationPreferences?.searchPostalCode?.trim();
    return fromLocationPrefs || fromProfile || "";
  }, [
    locationPrefs?.postalCode,
    userProfile?.locationPreferences?.searchPostalCode,
  ]);

  const tasks = useMemo(() => {
    return getOnboardingTasks({
      username: userProfile?.username,
      generatedUsername,
      hasPostalCode: Boolean(postalCode),
      hasAvatar: Boolean(userProfile?.images?.length),
      hasBio: Boolean(userProfile?.bio && userProfile.bio.trim().length > 0),
      hasProfessionalAccount: Boolean(activeOrg?.id),
    });
  }, [
    userProfile?.username,
    generatedUsername,
    postalCode,
    userProfile?.images?.length,
    userProfile?.bio,
    activeOrg?.id,
  ]);

  const completedCount = countCompletedTasks(tasks);
  const totalCount = tasks.length;
  const tasksRemaining = totalCount - completedCount;

  useEffect(() => {
    if (open && !hasLoggedImpression) {
      analytics.event({
        action: "onboarding.todo_modal_impression",
        category: "onboarding",
        custom_parameters: {
          tasksRemaining,
          userId: user?.uid,
        },
      });
      setHasLoggedImpression(true);
    }
  }, [open, hasLoggedImpression, tasksRemaining, user?.uid]);

  const closeModal = useCallback(() => {
    if (hasClosedRef.current) return;
    hasClosedRef.current = true;
    setOpen(false);
    onDismiss();
  }, [onDismiss]);

  const handleClose = useCallback(
    (reason: CloseReason) => {
      if (reason === "skip" || reason === "dismiss") {
        analytics.event({
          action: "onboarding.todo_modal_skip",
          category: "onboarding",
          custom_parameters: {
            reason,
            tasksRemaining,
            userId: user?.uid,
          },
        });
      } else if (reason === "complete") {
        analytics.event({
          action: "onboarding.todo_modal_complete",
          category: "onboarding",
          custom_parameters: {
            tasksRemaining,
            userId: user?.uid,
          },
        });
      }

      closeModal();
    },
    [closeModal, tasksRemaining, user?.uid],
  );

  const handleOpenChange = useCallback(
    (nextOpen: boolean) => {
      if (!nextOpen) {
        handleClose("dismiss");
      }
    },
    [handleClose],
  );

  const profileUsername =
    userProfile?.username?.trim() || generatedUsername?.trim();

  const profileEditHref = profileUsername
    ? routes.user.profileEdit(profileUsername)
    : routes.profile;

  const handleEditProfile = () => {
    analytics.event({
      action: "onboarding.todo_modal_cta",
      category: "onboarding",
      custom_parameters: {
        cta_action: "edit_profile",
        tasksRemaining,
        userId: user?.uid,
      },
    });
    closeModal();
    router.push(profileEditHref);
  };

  const handleSkip = () => {
    handleClose("skip");
  };

  useEffect(() => {
    if (open && tasksRemaining === 0) {
      handleClose("complete");
    }
  }, [handleClose, open, tasksRemaining]);

  const renderTask = (task: OnboardingTask) => {
    const iconClass = task.completed ? "text-brand-600" : "text-brand-300";

    return (
      <li
        key={task.key}
        className="border-brand-100 flex items-start gap-3 bg-white/90 p-4 not-last:border-b"
      >
        <div className="mt-1">
          {task.completed ? (
            <CheckCircle2 aria-hidden className={cn("h-5 w-5", iconClass)} />
          ) : (
            <Circle aria-hidden className={cn("h-5 w-5", iconClass)} />
          )}
        </div>
        <div className="flex flex-1 flex-col gap-1">
          <div className="flex flex-wrap items-center gap-2">
            <span className="text-brand-900 font-medium">{task.label}</span>
            {task.optional && (
              <Badge className="bg-brand-100 text-brand-700 border-0">
                Optional
              </Badge>
            )}
          </div>
          {task.description ? (
            <p className="text-brand-700 text-sm">{task.description}</p>
          ) : null}
        </div>
      </li>
    );
  };

  return (
    <Dialog open={open} onOpenChange={handleOpenChange}>
      <DialogPopup className="bg-cream-50/95 max-w-xl border-none p-0 shadow-xl sm:rounded-3xl">
        <div className="flex flex-col gap-6 p-6 sm:p-8">
          <DialogHeader className="space-y-3">
            <div className="text-brand-600 flex items-center gap-1">
              <Logo symbolOnly className="size-4" aria-hidden />
              <span className="text-sm font-medium tracking-wide">
                Welcome to Local
              </span>
            </div>
            <DialogTitle className="text-brand-900 font-serif text-2xl sm:text-3xl">
              Let&apos;s get your profile ready
            </DialogTitle>
            <DialogDescription className="text-brand-700">
              Complete these quick steps to personalize your experience and
              discover the best of your community.
            </DialogDescription>
          </DialogHeader>

          <div className="space-y-4">
            <ul className="bg-card space-y-0 rounded-2xl p-4 shadow-sm">
              {tasks.map(renderTask)}
            </ul>
            <div className="flex flex-col gap-3 sm:flex-row sm:items-center sm:justify-between">
              <Button
                variant="ghost"
                onClick={handleSkip}
                className="text-brand-700 hover:bg-brand-100/60"
              >
                Skip for now
              </Button>
              <Button variant="link" asChild>
                <Link href={profileEditHref} onClick={handleEditProfile}>
                  Edit Profile
                </Link>
              </Button>
            </div>
          </div>
        </div>
      </DialogPopup>
    </Dialog>
  );
}

export function NewUserTodoModal() {
  const [newUser, setNewUser] = useQueryState("new_user");
  const [generatedUsernameFromQuery, setGeneratedUsername] =
    useQueryState("generated_username");
  const [shouldRender, setShouldRender] = useState(false);
  const [capturedGeneratedUsername, setCapturedGeneratedUsername] = useState<
    string | null
  >(null);

  useEffect(() => {
    if (!newUser || shouldRender) {
      return;
    }

    // Capture the generated username before clearing query params
    setCapturedGeneratedUsername(generatedUsernameFromQuery);
    setShouldRender(true);

    // Clear query params after reading them
    setNewUser(null);
    setGeneratedUsername(null);
  }, [
    newUser,
    generatedUsernameFromQuery,
    setNewUser,
    setGeneratedUsername,
    shouldRender,
  ]);

  if (!shouldRender) {
    return null;
  }

  return (
    <ModalContent
      generatedUsername={capturedGeneratedUsername || undefined}
      onDismiss={() => {
        setShouldRender(false);
      }}
    />
  );
}

export default NewUserTodoModal;
